import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import { Treatment } from "./TreatmentPlanStep";

interface AddTreatmentModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (treatment: Treatment) => void;
  existingNames?: string[];
}

const AddTreatmentModal: React.FC<AddTreatmentModalProps> = ({
  open,
  onClose,
  onAdd,
  existingNames = [],
}) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const handleClose = () => {
    setName("");
    setError("");
    onClose();
  };

  const handleAdd = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Please enter a treatment name");
      return;
    }

    const isDuplicate = existingNames.some(
      (n) => n.toLowerCase() === trimmedName.toLowerCase(),
    );
    if (isDuplicate) {
      setError("This treatment already exists");
      return;
    }

    onAdd({
      id: `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: trimmedName,
      category: "other",
      priority: "first-line",
      rationale: "",
      linkedDiagnoses: [],
    });

    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Treatment</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          Add a treatment to consider in the plan.
        </DialogContentText>
        <TextField
          autoFocus
          label="Treatment name"
          placeholder="e.g., Amoxicillin 500mg PO TID"
          fullWidth
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError("");
          }}
          error={!!error}
          helperText={error}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleAdd} variant="contained">
          Add to Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTreatmentModal;
